import { useState, useMemo } from 'react'
import { router, Link } from '@inertiajs/react'
import AppLayout from '../../components/AppLayout'
import { useTheme } from '../../contexts/ThemeContext'

// ── 指標キー → カテゴリ分類 ──────────────────────────────────────────
const INDICATOR_CATEGORIES = [
  { key: 'seasonality',  label: '旬・推し',       indicatorKeys: ['seasonal'] },
  { key: 'popularity',   label: '人気',           indicatorKeys: ['popularity', 'best_one'] },
  { key: 'rarity',       label: '珍しさ',         indicatorKeys: ['standard_veggie', 'special_veggie', 'rare_veggie'] },
  { key: 'balance',      label: 'セットバランス', indicatorKeys: ['color_variety', 'small_volume', 'large_volume'] },
  { key: 'duplication',  label: '重複状況',       indicatorKeys: ['recent_appearance', 'previous_overlap'] },
]

// 珍しさ区分の5段階定義（rarity_score は "-2"〜"2" の数値文字列）
const RARITY_LEVELS = [
  { key: '-2', label: '定番',    bg: '#3a5a3a', text: '#fff' },
  { key: '-1', label: '準定番',  bg: '#5b7d4f', text: '#fff' },
  { key: '0',  label: '標準',    bg: '#c8ccc4', text: '#444' },
  { key: '1',  label: '準珍',    bg: '#c9963f', text: '#fff' },
  { key: '2',  label: '珍しい',  bg: '#c0463a', text: '#fff' },
]

function rarityLabel(rarity) {
  const key = rarity != null ? String(rarity) : '0'
  return RARITY_LEVELS.find(r => r.key === key) || RARITY_LEVELS[2]
}

// 色名（漢字1文字）→ CSS hex 変換
const COLOR_HASH = {
  赤: '#e60033', 黒: '#2b2b2b', 紫: '#65318e', 白: '#f8fbf8', 橙: '#f08300',
  緑: '#3eb370', 薄: '#69b076', 深: '#00552e', 黄: '#ffec47', 茶: '#783c1d',
  桃: '#f09199', 桜: '#fef4f4', 茜: '#b7282e', 肌: '#fce2c4', 若: '#98d98e',
  浅: '#88cb7f', 青: '#00a497', 紺: '#223a70', 菫: '#7058a3', 紅: '#d7003a',
  苗: '#b0ca71', 柳: '#a8c97f', 錆: '#6c3524', 栗: '#762f07', 褐: '#8a3b00',
  金: '#e6b422',
}

function colorHex(name) {
  return name ? (COLOR_HASH[name] || null) : null
}

// カテゴリ別カラー定義（スコア内訳バー共通）
const CATEGORY_META = {
  seasonality: { label: '旬・推し',       color: '#3a6644' },
  popularity:  { label: '人気',           color: '#d4762a' },
  rarity:      { label: '珍しさ',         color: '#8a6858' },
  balance:     { label: 'セットバランス', color: '#5a6e9a' },
  duplication: { label: '重複状況',       color: '#7a8a78' },
}

// カテゴリ別内訳スタックバー（delivery_score の構成を色で表現、絶対値表示）
function ScoreBreakdownBar({ breakdown, height = 7, C }) {
  if (!breakdown) return null
  return (
    <div style={{ display: 'flex', height, borderRadius: height / 2, overflow: 'hidden', background: C.lineSoft, gap: '1px' }}>
      {INDICATOR_CATEGORIES.map(cat => {
        const pts = breakdown[cat.key] || 0
        if (!pts) return null
        const color = CATEGORY_META[cat.key]?.color || C.inkMuted
        return (
          <div key={cat.key} title={`${CATEGORY_META[cat.key]?.label}: ${pts.toFixed(1)}pt`}
            style={{ width: `${pts}%`, background: color, flexShrink: 0 }} />
        )
      })}
    </div>
  )
}

// ヒーロー用: 内訳凡例 + バー
function DeliveryBreakdownLegend({ details, deliveryScore, C }) {
  if (!details || !details.length || !deliveryScore) return null
  const breakdown = {}
  INDICATOR_CATEGORIES.forEach(cat => {
    breakdown[cat.key] = cat.indicatorKeys.reduce((s, k) => {
      const d = details.find(x => x.key === k)
      return s + (d?.weighted_delivery || 0)
    }, 0) * 100
  })
  return (
    <div>
      <ScoreBreakdownBar breakdown={breakdown} height={8} C={C} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px', marginTop: '8px' }}>
        {INDICATOR_CATEGORIES.map(cat => {
          const pts = breakdown[cat.key] || 0
          if (!pts) return null
          const color = CATEGORY_META[cat.key]?.color
          return (
            <span key={cat.key} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: C.inkSoft }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: color, display: 'inline-block', flexShrink: 0 }} />
              {cat.label}
              <span style={{ fontFamily: "'JetBrains Mono', monospace", color: C.ink, fontWeight: 600 }}>{pts.toFixed(1)}</span>
            </span>
          )
        })}
      </div>
    </div>
  )
}

function scoreColor(v, C) {
  if (v == null) return C.inkMuted
  const p = v * 100
  return p >= 80 ? C.greenDark : p >= 70 ? C.gold : p >= 60 ? C.carrot : C.tomato
}

// 満足度: 80/60 で3段階
function satisfactionColor(v, C) {
  if (v == null) return C.inkMuted
  const p = v * 100
  return p >= 80 ? C.greenDark : p >= 60 ? C.gold : C.tomato
}

// ベストワン率: 比率が小さい傾向なので閾値を調整
function bestOneColor(v, C) {
  if (v == null) return C.inkMuted
  const p = v * 100
  return p >= 25 ? C.greenDark : p >= 12 ? C.gold : C.tomato
}

function categoryScore(result, indicatorKeys) {
  const details = (result.details || []).filter(d => indicatorKeys.includes(d.key))
  if (!details.length) return null
  const withScore = details.filter(d => d.adjusted_score != null)
  if (!withScore.length) return null
  return withScore.reduce((s, d) => s + d.adjusted_score, 0) / withScore.length
}

// ── スロットの代替バッジ ───────────────────────────────────────────────
function MultiBadge({ count, C }) {
  return (
    <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '9px', background: C.carrotSoft, color: C.carrot, fontWeight: 500, whiteSpace: 'nowrap' }}>
      代替 {count}品
    </span>
  )
}

// ── スロット行の共通ラッパー ──────────────────────────────────────────
// ヘッダー行なし。single は plain row、multi は左ボーダーでグルーピング表現。
function SlotVisualCard({ slot, slotIdx, renderCandidate, C }) {
  const { candidates, is_multi } = slot

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '26px 1fr', gap: '10px', alignItems: 'start', marginBottom: '4px', paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}` }}>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: C.inkMuted, textAlign: 'right', paddingTop: '7px' }}>
        {String(slotIdx + 1).padStart(2, '0')}
      </span>
      {is_multi ? (
        <div style={{ borderLeft: `3px solid ${C.carrot}`, paddingLeft: '10px', borderRadius: '0 3px 3px 0' }}>
          {candidates.map((c, ci) => (
            <div key={ci} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '4px 0', borderBottom: ci < candidates.length - 1 ? `1px dashed ${C.lineSoft}` : 'none' }}>
              {renderCandidate(c)}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '4px 0' }}>
          {renderCandidate(candidates[0])}
        </div>
      )}
    </div>
  )
}

function BigScoreRing({ score, size = 140, C }) {
  const r = size / 2 - 10
  const circ = 2 * Math.PI * r
  const pct = score != null ? score * 100 : 0
  const dash = (pct / 100) * circ
  const color = scoreColor(score, C)
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={C.lineSoft} strokeWidth="10"/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"/>
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center' }}>
        <div>
          <div style={{ fontFamily: "'Noto Serif JP', serif", fontSize: '38px', fontWeight: 500, color, lineHeight: 1 }}>
            {score != null ? Math.round(score * 100) : '—'}
          </div>
          <div style={{ fontSize: '11px', color: C.inkMuted, letterSpacing: '0.1em', marginTop: '2px' }}>/ 100</div>
        </div>
      </div>
    </div>
  )
}

function QuickBar({ categories, activeKey, onSelect, result, C }) {
  return (
    <div style={{ display: 'flex', borderBottom: `1px solid ${C.line}`, background: C.paper, overflowX: 'auto' }}>
      {categories.map(cat => {
        const score = categoryScore(result, cat.indicatorKeys)
        const scoreNum = score != null ? Math.round(score * 100) : null
        const isActive = activeKey === cat.key
        const tabScoreColor = scoreNum == null ? C.inkMuted : scoreNum >= 80 ? C.greenDark : scoreNum >= 70 ? C.gold : scoreNum >= 60 ? C.carrot : C.tomato
        return (
          <button
            key={cat.key}
            onClick={() => onSelect(cat.key)}
            style={{
              fontFamily: "'Noto Sans JP', sans-serif", fontSize: '13px', fontWeight: 500,
              padding: '14px 22px', color: isActive ? C.ink : C.inkMuted,
              border: 'none', borderRight: `1px solid ${C.lineSoft}`,
              background: isActive ? C.paper2 : 'transparent',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
              flexShrink: 0,
              borderBottom: isActive ? `2px solid ${C.carrot}` : 'none',
            }}
          >
            {cat.label}
            <span style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: '11px',
              padding: '2px 6px', borderRadius: '3px',
              background: isActive ? C.paper : C.paper2,
              color: scoreNum == null ? C.inkMuted : tabScoreColor,
            }}>
              {scoreNum ?? '—'}
            </span>
          </button>
        )
      })}
    </div>
  )
}

function IndicatorRow({ detail, isLast, C }) {
  const dirLabel = { positive: '加点', negative: '減点', add: '加点', reduce: '減点' }
  const isPositive = detail.direction === 'positive' || detail.direction === 'add'
  const dirColor = isPositive ? C.greenDark : C.tomato
  const dirBg    = isPositive ? C.greenPale  : '#fde8e8'
  const w = detail.adjusted_score != null ? Math.round(detail.adjusted_score * 100) : 0
  const barColor = isPositive ? C.green : C.carrot

  return (
    <div style={{ paddingBottom: isLast ? 0 : '14px', marginBottom: isLast ? 0 : '14px', borderBottom: isLast ? 'none' : `1px solid ${C.lineSoft}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13.5px', fontWeight: 500, color: C.ink }}>{detail.name}</span>
          <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '3px', background: dirBg, color: dirColor, fontWeight: 500 }}>
            {dirLabel[detail.direction] || detail.direction}
          </span>
        </div>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11.5px', color: C.inkMuted }}>
          生スコア {detail.raw_score != null ? detail.raw_score.toFixed(3) : '—'}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ flex: 1, height: '5px', background: C.lineSoft, borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${w}%`, background: barColor, borderRadius: '3px', transition: 'width 0.4s ease' }} />
        </div>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', color: scoreColor(detail.adjusted_score, C), minWidth: '32px', textAlign: 'right' }}>
          {w}
        </span>
      </div>
      <div style={{ marginTop: '4px', fontSize: '11px', color: C.inkMuted, display: 'flex', gap: '14px' }}>
        {detail.weight_delivery != null && (
          <span>お届け重み {Math.round(detail.weight_delivery * 100)}%
            {detail.weighted_delivery != null && ` → +${(detail.weighted_delivery * 100).toFixed(1)}pt`}
          </span>
        )}
        {detail.weight_appeal != null && (
          <span>魅力重み {Math.round(detail.weight_appeal * 100)}%
            {detail.weighted_appeal != null && ` → +${(detail.weighted_appeal * 100).toFixed(1)}pt`}
          </span>
        )}
      </div>
    </div>
  )
}

// ── 旬度・推し ──────────────────────────────────────────────────────────
function SeasonalityVisual({ slots, C }) {
  const statusStyle = {
    '推し旬': { bg: C.gold,     color: C.paper },
    '旬':     { bg: C.green,    color: C.paper },
    '避':     { bg: C.lineSoft, color: C.inkMuted },
    '通常':   { bg: C.lineSoft, color: C.inkMuted },
  }
  const statusLabel = { '推し旬': '推し旬', '旬': '旬', '避': '時期外', '通常': '—' }

  const renderCandidate = (c) => {
    const st = c.season_status ? (statusStyle[c.season_status] || statusStyle['通常']) : null
    return (
      <>
        <span style={{ flex: 1, fontSize: '13px', color: C.ink }}>{c.name || c.product_code}</span>
        {st ? (
          <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', background: st.bg, color: st.color, whiteSpace: 'nowrap' }}>
            {statusLabel[c.season_status] || c.season_status}
          </span>
        ) : (
          <span style={{ fontSize: '10px', color: C.inkMuted }}>—</span>
        )}
      </>
    )
  }

  if (!slots.length) return <div style={{ color: C.inkMuted, fontSize: '13px' }}>旬データがありません</div>
  return (
    <div>
      {slots.map((slot, i) => (
        <SlotVisualCard key={i} slot={slot} slotIdx={i} renderCandidate={renderCandidate} C={C} />
      ))}
    </div>
  )
}

// ── 重複状況 ─────────────────────────────────────────────────────────────
const DAY_ORDER  = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat']
const DAY_LABELS = { mon: '月', tue: '火', wed: '水', thu: '木', fri: '金', sat: '土' }

function deliveryDayText(days) {
  if (!days || !days.length) return null
  // お届けなしの曜日に全角スペースを入れて縦揃え
  return DAY_ORDER.map(d => days.includes(d) ? DAY_LABELS[d] : '　').join('')
}

// 直近 lookback 回中の許容上限 = floor(lookback / idealFreq)
const RECENT_LOOKBACK = 7

function recentThreshold(idealFreq) {
  if (!idealFreq || idealFreq <= 0) return null
  return Math.floor(RECENT_LOOKBACK / idealFreq)
}

function DuplicationVisual({ slots, pastSets, C }) {
  const currentCodes = new Set(slots.flatMap(s => s.candidates.map(c => c.product_code)))

  const renderCandidate = (c) => {
    const hasOverlap    = !!c.previous_overlap_status
    const recentCount   = c.recent_appearance_count || 0
    const threshold     = recentThreshold(c.planning_frequency)
    const isOver        = threshold != null ? recentCount > threshold : false
    const countColor    = isOver ? C.carrot  : C.greenDark
    const countBg       = isOver ? C.carrotSoft : C.greenPale
    const overlapLabel  = pastSets.find(ps => ps.week === c.previous_overlap_week)?.label
    // 前回=1期前, 前々回=2期前。経過期数 >= planning_frequency なら許容範囲内でバッジ非表示
    const periodsAgo    = overlapLabel === '前回' ? 1 : overlapLabel === '前々回' ? 2 : null
    const overlapOk     = periodsAgo != null && c.planning_frequency != null && periodsAgo >= c.planning_frequency
    const showOverlap   = hasOverlap && !overlapOk

    return (
      <>
        {/* 品目名 */}
        <span style={{ flex: 1, fontSize: '13px', color: showOverlap ? C.carrot : C.ink, fontWeight: showOverlap ? 500 : 400 }}>
          {c.name || c.product_code}
        </span>
        {/* 配送曜日 */}
        {deliveryDayText(c.delivery_days) && (
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: C.inkMuted, background: C.paper2, padding: '1px 6px', borderRadius: '4px', whiteSpace: 'nowrap', flexShrink: 0, letterSpacing: '0.05em' }}>
            {deliveryDayText(c.delivery_days)}
          </span>
        )}
        {/* お届け頻度設定値 */}
        {c.planning_frequency && (
          <span style={{ fontSize: '10px', color: C.inkMuted, whiteSpace: 'nowrap', flexShrink: 0 }}>
            お届け頻度：{c.planning_frequency}回に1回
          </span>
        )}
        {/* 重複バッジ（直近回数の左隣）：許容頻度内なら非表示 */}
        {showOverlap && (
          <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '4px', background: C.carrot, color: '#fff', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {overlapLabel ? `${overlapLabel}と` : ''}{c.previous_overlap_status}
          </span>
        )}
        {/* 直近登場回数バッジ（右端） */}
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '1px', padding: '2px 8px', borderRadius: '4px', background: countBg, flexShrink: 0 }}>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '18px', fontWeight: 700, color: countColor, lineHeight: 1 }}>{recentCount}</span>
          <span style={{ fontSize: '9px', color: countColor, lineHeight: 1 }}>回</span>
          {threshold != null && (
            <span style={{ fontSize: '9px', color: C.inkMuted, marginLeft: '5px' }}>上限{threshold}</span>
          )}
        </span>
      </>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* 今回のセット（品目行） */}
      <div>
        {/* 凡例ヘッダー */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '10px', padding: '6px 10px', background: C.paper2, borderRadius: '6px', fontSize: '11px', color: C.inkMuted }}>
          <span>直近{RECENT_LOOKBACK}回の立案での登場回数</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '2px', background: C.greenPale, border: `1px solid ${C.green}` }} />
            <span style={{ color: C.greenDark }}>上限以内（理想頻度÷{RECENT_LOOKBACK}）</span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '2px', background: C.carrotSoft, border: `1px solid ${C.carrot}` }} />
            <span style={{ color: C.carrot }}>上限超過</span>
          </span>
        </div>
        {slots.map((slot, i) => (
          <SlotVisualCard key={i} slot={slot} slotIdx={i} renderCandidate={renderCandidate} C={C} />
        ))}
      </div>

      {/* 前回・前々回のセット内容 */}
      {pastSets.map((ps) => (
        <div key={ps.week}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: C.inkMuted, letterSpacing: '0.08em', marginBottom: '8px' }}>
            {ps.label}（{ps.week}）
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {ps.products.map((p, i) => {
              const isOverlap = currentCodes.has(p.product_code)
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '3px 8px', borderRadius: '4px',
                  background: isOverlap ? C.carrotSoft : 'transparent',
                }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: C.inkMuted, minWidth: '20px', textAlign: 'right' }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span style={{ fontSize: '13px', color: isOverlap ? C.carrot : C.inkSoft, fontWeight: isOverlap ? 600 : 400 }}>
                    {p.name}
                  </span>
                  {isOverlap && (
                    <span style={{ fontSize: '10px', color: C.carrot, fontWeight: 600 }}>← 今回と重複</span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── 珍しさバランス ───────────────────────────────────────────────────────
// 5段階を5セルのゲージで表現するコンポーネント
function RarityGauge({ rarityScore }) {
  const key = rarityScore != null ? String(rarityScore) : null
  const activeIdx = key != null ? RARITY_LEVELS.findIndex(r => r.key === key) : -1
  return (
    <div style={{ display: 'flex', gap: '3px', alignItems: 'center', flexShrink: 0 }}>
      {RARITY_LEVELS.map((r, i) => (
        <div
          key={r.key}
          title={r.label}
          style={{
            width: '14px', height: '22px', borderRadius: '3px',
            background: i === activeIdx ? r.bg : '#ddd',
            opacity: i === activeIdx ? 1 : 0.35,
          }}
        />
      ))}
    </div>
  )
}

function RarityVisual({ slots, C }) {
  // スロット代表（先頭候補）の珍しさ分布を集計
  const counts = {}
  RARITY_LEVELS.forEach(r => { counts[r.key] = 0 })
  slots.forEach(slot => {
    const rep = slot.candidates[0]
    const k = rep?.rarity_score != null ? String(rep.rarity_score) : null
    if (k && counts[k] != null) counts[k]++
  })
  const total = slots.length
  if (!total) return <div style={{ color: C.inkMuted, fontSize: '13px' }}>品目データがありません</div>

  const renderCandidate = (c) => (
    <>
      <span style={{ flex: 1, fontSize: '13px', color: C.ink, display: 'flex', alignItems: 'center', gap: '6px' }}>
        {c.name || c.product_code}
        {c.volume && (
          <span style={{ fontSize: '10px', padding: '1px 5px', borderRadius: '3px', background: C.paper2, border: `1px solid ${C.line}`, color: C.inkMuted, flexShrink: 0 }}>
            {c.volume}
          </span>
        )}
      </span>
      <RarityGauge rarityScore={c.rarity_score} />
    </>
  )

  return (
    <div>
      {/* 5段階分布バー（スロット代表の集計） */}
      <div style={{ display: 'flex', height: '28px', borderRadius: '6px', overflow: 'hidden', marginBottom: '14px', border: `1px solid ${C.line}` }}>
        {RARITY_LEVELS.map(r => {
          const n = counts[r.key] || 0
          if (!n) return null
          return (
            <div key={r.key} style={{ flex: n, background: r.bg, display: 'grid', placeItems: 'center', fontSize: '11px', color: r.text, fontWeight: 500 }}>
              {r.label} {n}品
            </div>
          )
        })}
      </div>
      {/* スロット別リスト */}
      <div>
        {slots.map((slot, i) => (
          <SlotVisualCard key={i} slot={slot} slotIdx={i} renderCandidate={renderCandidate} C={C} />
        ))}
      </div>
    </div>
  )
}

// ── 人気 ─────────────────────────────────────────────────────────────────
function PopularityVisual({ slots, C }) {
  const hasData = slots.some(s => s.candidates.some(c => c.satisfaction_score != null || c.best_one_ratio != null))
  if (!hasData) return <div style={{ color: C.inkMuted, fontSize: '13px' }}>人気データがありません</div>

  const renderCandidate = (c) => (
    <>
      <span style={{ color: C.ink, minWidth: '130px', fontSize: '12px' }}>{c.name || c.product_code}</span>
      {c.satisfaction_score != null && (
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: C.greenDark }}>
          {(c.satisfaction_score * 100).toFixed(0)}%
          {c.satisfaction_count != null && <span style={{ color: C.inkMuted }}> ({c.satisfaction_count}件)</span>}
        </span>
      )}
      {c.best_one_ratio != null && (
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: C.gold }}>
          ベストワン {(c.best_one_ratio * 100).toFixed(1)}%
          {c.best_one_total > 0 && <span style={{ color: C.inkMuted }}> ({c.best_one_wins}/{c.best_one_total}件)</span>}
        </span>
      )}
    </>
  )

  return (
    <div>
      {slots.map((slot, i) => (
        <SlotVisualCard key={i} slot={slot} slotIdx={i} renderCandidate={renderCandidate} C={C} />
      ))}
    </div>
  )
}

// ── セットバランス（色彩） ────────────────────────────────────────────────
function ColorVisual({ slots, C }) {
  // スロット代表の色のみ使用（代替候補の色は重複カウントしない）
  const colorEntries = slots.flatMap(s => {
    const rep = s.candidates[0]
    return [
      rep?.color1 ? { name: rep.color1, hex: colorHex(rep.color1) } : null,
      rep?.color2 ? { name: rep.color2, hex: colorHex(rep.color2) } : null,
    ].filter(Boolean)
  })

  if (!colorEntries.length) {
    return (
      <div>
        <div style={{ color: C.inkMuted, fontSize: '13px', marginBottom: '10px' }}>色データがありません</div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {slots.map((s, i) => (
            <span key={i} style={{ fontSize: '12px', padding: '4px 10px', background: C.paper2, border: `1px solid ${C.line}`, borderRadius: '4px', color: C.ink }}>
              {s.candidates[0]?.name || s.candidates[0]?.product_code}
            </span>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
        {colorEntries.map((c, i) => (
          <div key={i} title={c.name} style={{ width: '32px', height: '32px', borderRadius: '4px', background: c.hex || C.lineSoft, border: '1px solid rgba(0,0,0,0.08)', display: 'grid', placeItems: 'center', fontSize: '10px', color: '#fff', textShadow: '0 0 2px rgba(0,0,0,0.6)', fontWeight: 700 }}>
            {c.name}
          </div>
        ))}
      </div>
      <div style={{ fontSize: '12px', color: C.inkSoft }}>{slots.length} スロット / {colorEntries.length} 色使用</div>
    </div>
  )
}

function IndicatorSection({ sectionNum, title, score, visual, comment, details, C }) {
  const scoreNum = score != null ? Math.round(score * 100) : null
  const sc = scoreColor(score, C)
  const [detailOpen, setDetailOpen] = useState(false)

  return (
    <div style={{ padding: '40px 56px', borderBottom: `1px dashed ${C.line}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '18px', marginBottom: '24px' }}>
        <span style={{ fontFamily: "'Noto Serif JP', serif", fontSize: '13px', color: C.carrot, letterSpacing: '0.3em' }}>
          {String(sectionNum).padStart(2, '0')}
        </span>
        <h3 style={{ fontFamily: "'Noto Serif JP', serif", fontSize: '22px', fontWeight: 500, margin: 0, color: C.ink }}>
          {title}
        </h3>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'baseline', gap: '6px' }}>
          <span style={{ fontFamily: "'Noto Serif JP', serif", fontSize: '28px', fontWeight: 500, color: sc }}>{scoreNum ?? '—'}</span>
          <span style={{ color: C.inkMuted, fontSize: '13px' }}>/100</span>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '36px', alignItems: 'start' }}>
        <div>{visual}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {comment && (
            <div style={{ background: C.paper2, borderLeft: `3px solid ${C.carrot}`, borderRadius: '0 6px 6px 0', padding: '14px 18px', fontSize: '13.5px', lineHeight: '1.75', color: C.ink }}>
              {comment}
            </div>
          )}
          {details && details.length > 0 && (
            <div>
              <button
                onClick={() => setDetailOpen(v => !v)}
                style={{ background: 'none', border: `1px solid ${C.line}`, borderRadius: '6px', padding: '5px 12px', fontSize: '11px', color: C.inkSoft, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', marginBottom: detailOpen ? '10px' : 0 }}
              >
                <span style={{ fontSize: '9px', transform: detailOpen ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block', transition: 'transform 0.15s' }}>▶</span>
                スコア明細 ({details.length}件)
              </button>
              {detailOpen && details.map((d, i) => (
                <IndicatorRow key={d.key} detail={d} isLast={i === details.length - 1} C={C} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── セット明細スロットカード ──────────────────────────────────────────────
function SlotGroupCard({ group, slotIdx, C }) {
  const { candidates, is_multi } = group

  const CandidateRow = ({ c }) => {
    const rl = rarityLabel(c.rarity_score)
    const dayText = deliveryDayText(c.delivery_days)
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '5px 0' }}>
        <span style={{ flex: 1, fontSize: '13px', color: C.ink, display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap', minWidth: '100px' }}>
          {c.color1 && <span title={c.color1} style={{ width: '8px', height: '8px', borderRadius: '50%', background: colorHex(c.color1) || C.lineSoft, border: '1px solid rgba(0,0,0,0.08)', display: 'inline-block', flexShrink: 0 }} />}
          {c.color2 && <span title={c.color2} style={{ width: '8px', height: '8px', borderRadius: '50%', background: colorHex(c.color2) || C.lineSoft, border: '1px solid rgba(0,0,0,0.08)', display: 'inline-block', flexShrink: 0 }} />}
          {c.name || c.product_code}
          {c.vegtype_flag === 'S' && <span style={{ fontSize: '10px', padding: '1px 5px', background: C.carrot, color: C.paper, borderRadius: '9px', fontWeight: 500 }}>S</span>}
          {c.season_status === '推し旬' && <span style={{ fontSize: '10px', padding: '1px 5px', background: C.tomato, color: C.paper, borderRadius: '9px', fontWeight: 500 }}>推し旬</span>}
          {c.season_status === '旬' && <span style={{ fontSize: '10px', padding: '1px 5px', background: C.gold, color: C.paper, borderRadius: '9px', fontWeight: 500 }}>旬</span>}
          {c.previous_overlap_status && <span style={{ fontSize: '10px', padding: '1px 5px', background: '#e8d5d2', color: C.tomato, borderRadius: '9px' }}>前回重複</span>}
        </span>
        <span style={{ fontSize: '10px', padding: '1px 8px', borderRadius: '4px', fontWeight: 500, background: rl.bg, color: rl.text, whiteSpace: 'nowrap', flexShrink: 0 }}>{rl.label}</span>
        {dayText ? (
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: C.inkMuted, background: C.paper2, padding: '1px 5px', borderRadius: '4px', whiteSpace: 'nowrap', flexShrink: 0, letterSpacing: '0.05em' }}>
            {dayText}
          </span>
        ) : <span style={{ minWidth: '52px', flexShrink: 0 }} />}
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: satisfactionColor(c.satisfaction_score, C), whiteSpace: 'nowrap', flexShrink: 0, minWidth: '34px', textAlign: 'right' }}>
          {c.satisfaction_score != null ? `${(c.satisfaction_score * 100).toFixed(0)}%` : '—'}
        </span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: bestOneColor(c.best_one_ratio, C), whiteSpace: 'nowrap', flexShrink: 0, minWidth: '44px', textAlign: 'right' }}>
          {c.best_one_ratio != null ? `★${(c.best_one_ratio * 100).toFixed(1)}%` : '—'}
        </span>
      </div>
    )
  }

  if (!is_multi) {
    const it = candidates[0]
    if (!it) return null
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '28px 1fr', gap: '8px', alignItems: 'center', padding: '2px 0', borderBottom: `1px solid ${C.lineSoft}` }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: C.inkMuted, textAlign: 'right' }}>
          {String(slotIdx + 1).padStart(2, '0')}
        </span>
        <CandidateRow c={it} />
      </div>
    )
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '28px 1fr', gap: '8px', alignItems: 'start', marginBottom: '4px', paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}` }}>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: C.inkMuted, textAlign: 'right', paddingTop: '8px' }}>
        {String(slotIdx + 1).padStart(2, '0')}
      </span>
      <div style={{ borderLeft: `3px solid ${C.lineSoft}`, paddingLeft: '8px' }}>
        {candidates.map((c, ci) => (
          <div key={ci} style={{ borderBottom: ci < candidates.length - 1 ? `1px dashed ${C.lineSoft}` : 'none' }}>
            <CandidateRow c={c} />
          </div>
        ))}
      </div>
    </div>
  )
}

export default function SetPlanShow({ current_week, set_plan, result, past_sets = [], weekday_scores = {} }) {
  const { theme: C } = useTheme()
  const [activeCategory, setActiveCategory] = useState(INDICATOR_CATEGORIES[0].key)

  // slot_groups が空の場合は snapshot_items からフォールバック用スロットを生成
  const rawSlots = result.slot_groups || []
  const baseSlots = rawSlots.length > 0
    ? rawSlots
    : (result.snapshot_items || []).map((it, i) => ({
        slot_number: i, is_multi: false,
        candidates: [it],
        avg_delivery: null, min_delivery: null, max_delivery: null,
        avg_appeal: null, min_appeal: null, max_appeal: null,
      }))

  // 並び順: おまけ品目（商品名に「おまけ」を含む）を末尾、それ以外はカテゴリコード順
  const slots = useMemo(() => [...baseSlots].sort((a, b) => {
    const aRep = a.candidates[0]
    const bRep = b.candidates[0]
    const aOmake = (aRep?.name || '').includes('おまけ')
    const bOmake = (bRep?.name || '').includes('おまけ')
    if (aOmake !== bOmake) return aOmake ? 1 : -1
    const aCat = aRep?.middle_category_code || 'zzz'
    const bCat = bRep?.middle_category_code || 'zzz'
    return aCat < bCat ? -1 : aCat > bCat ? 1 : 0
  }), [baseSlots])

  // 全候補の flat list（ヒーロー文生成・genComment 用）
  const allCandidates = slots.flatMap(s => s.candidates)
  const totalCandidates = allCandidates.length
  const multiSlotCount  = slots.filter(s => s.is_multi).length

  const handleReEvaluate = () => {
    if (confirm('再評価しますか？')) {
      router.post(`/weekly_evaluations/${current_week}/set_plans/${set_plan.id}/evaluate`, {}, { replace: true })
    }
  }

  function genComment(catKey) {
    const overlapItems = allCandidates.filter(it => it.previous_overlap_status)
    switch (catKey) {
      case 'seasonality': {
        const oshi = allCandidates.filter(it => it.season_status === '推し旬').length
        const shun = allCandidates.filter(it => it.season_status === '旬').length
        if (!oshi && !shun) return '旬・推し旬の品目が見つかりませんでした。旬野菜の追加を検討してください。'
        return `${slots.length}スロット中 推し旬${oshi}品・旬${shun}品が含まれています。旬野菜の充実度は評価に大きく影響します。`
      }
      case 'duplication': {
        const overlapSlots = slots.filter(s => s.candidates.some(c => c.previous_overlap_status))
        if (!overlapSlots.length) return '前回との品目重複はありません。重複度スコアは良好です。'
        return `${overlapSlots.length}スロットで前回セットと重複しています。重複度スコアが下がる主要因です。`
      }
      case 'rarity': {
        const s = allCandidates.filter(it => it.vegtype_flag === 'S').length
        const t = allCandidates.filter(it => it.vegtype_flag === 'T').length
        if (!s && !t) return '珍しさデータがありません（vegtype未設定）。マスタの整備をお勧めします。'
        return `推し野菜(S)${s}品、定番(T)${t}品の構成です。S野菜の存在感が魅力スコアを押し上げます。`
      }
      case 'popularity':
        return 'お客様の満足度データに基づいて算出しています。ベストワン候補の選定が魅力スコアの鍵です。'
      case 'balance':
        return '色味の多様性とボリュームバランスを評価します。緑系に偏りすぎないよう、彩り豊かな構成を心がけましょう。'
      default:
        return null
    }
  }

  return (
    <AppLayout bgColor={C.bg} maxWidth="1200px" noPadding>
      {/* パンくず */}
      <div style={{ padding: '20px 32px 0', fontSize: '13px', color: C.inkSoft }}>
        <Link href={`/weekly_evaluations/${current_week}`} style={{ color: C.greenDark, textDecoration: 'none' }}>
          週次評価 {current_week}
        </Link>
        <span style={{ margin: '0 8px', color: C.inkMuted }}>／</span>
        <span style={{ color: C.inkSoft }}>{set_plan.set_type_name || set_plan.plan_code}</span>
      </div>

      <div style={{
        margin: '16px 32px 48px', background: C.paper, borderRadius: '14px',
        boxShadow: '0 12px 40px rgba(60,50,30,0.10)', border: `1px solid ${C.line}`,
        overflow: 'hidden',
      }}>
        {/* アプリバー */}
        <div style={{ background: C.paper2, borderBottom: `1px solid ${C.line}`, padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: C.greenDark, color: C.paper, display: 'grid', placeItems: 'center', fontFamily: "'Noto Serif JP', serif", fontWeight: 700, fontSize: '14px' }}>坂</div>
            <span style={{ fontFamily: "'Noto Serif JP', serif", fontWeight: 500, fontSize: '15px', color: C.ink }}>野菜セット評価値シュミレータ</span>
            <span style={{ color: C.inkMuted, fontSize: '12px' }}>/ ストーリービュー</span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Link
              href={`/set_planning/${current_week}/${set_plan.id}`}
              style={{ fontFamily: 'inherit', fontSize: '12px', fontWeight: 500, padding: '6px 14px', borderRadius: '5px', border: `1px solid ${C.greenDark}`, background: C.greenPale, color: C.greenDark, cursor: 'pointer', textDecoration: 'none' }}
            >
              立案支援
            </Link>
            <button
              onClick={handleReEvaluate}
              style={{ fontFamily: 'inherit', fontSize: '12px', fontWeight: 500, padding: '6px 14px', borderRadius: '5px', border: `1px solid ${C.line}`, background: C.paper, color: C.inkSoft, cursor: 'pointer' }}
            >
              再評価
            </button>
          </div>
        </div>

        {/* ヒーロー */}
        <div style={{
          padding: '48px 56px 36px',
          background: `linear-gradient(135deg, ${C.paper2} 0%, ${C.paper} 100%)`,
          borderBottom: `1px solid ${C.line}`, position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', right: -100, top: -100, width: 360, height: 360, borderRadius: '50%', background: `radial-gradient(circle, ${C.greenPale} 0%, transparent 70%)`, opacity: 0.55, pointerEvents: 'none' }} />
          <div style={{ fontSize: '11px', letterSpacing: '0.3em', color: C.inkMuted, textTransform: 'uppercase', marginBottom: '10px' }}>
            SET DIAGNOSIS &nbsp;/&nbsp; {result.delivery_week || current_week}
          </div>
          <h2 style={{ fontFamily: "'Noto Serif JP', serif", fontSize: '28px', fontWeight: 500, margin: '0 0 32px', color: C.ink, display: 'flex', alignItems: 'center', gap: '16px' }}>
            {result.set_type_name || set_plan.set_type_name || result.set_plan_code}
            <span style={{ fontSize: '13px', color: C.inkSoft, padding: '5px 14px', background: C.paper, border: `1px solid ${C.line}`, borderRadius: '999px', fontFamily: "'Noto Sans JP', sans-serif", fontWeight: 400 }}>
              {result.set_plan_code}
            </span>
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '48px', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <div style={{ display: 'flex', gap: '32px' }}>
                <div style={{ textAlign: 'center' }}>
                  <BigScoreRing score={result.delivery_score} C={C} />
                  <div style={{ fontFamily: "'Noto Serif JP', serif", fontSize: '13px', color: C.inkSoft, marginTop: '8px' }}>お届けスコア</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <BigScoreRing score={result.appeal_score} C={C} />
                  <div style={{ fontFamily: "'Noto Serif JP', serif", fontSize: '13px', color: C.inkSoft, marginTop: '8px' }}>魅力スコア</div>
                </div>
              </div>
              <div style={{ width: '100%' }}>
                <DeliveryBreakdownLegend details={result.details} deliveryScore={result.delivery_score} C={C} />
              </div>
            </div>
            <div style={{ maxWidth: '520px' }}>
              <p style={{ fontFamily: "'Noto Serif JP', serif", fontSize: '17px', lineHeight: '1.8', color: C.ink, margin: '0 0 12px' }}>
                {slots.length > 0 ? (
                  <>
                    {slots.length}スロットのセット。
                    {allCandidates.filter(it => it.season_status === '推し旬').length > 0 && (
                      <>推し旬野菜 <span style={{ background: `linear-gradient(transparent 60%, ${C.greenPale} 60%)` }}>
                        {allCandidates.filter(it => it.season_status === '推し旬').map(it => it.name || it.product_code).join('・')}
                      </span> が存在感を発揮。</>
                    )}
                    {slots.filter(s => s.candidates.some(c => c.previous_overlap_status)).length > 0 && (
                      <><span style={{ background: `linear-gradient(transparent 60%, ${C.carrotSoft} 60%)` }}>
                        前回重複 {slots.filter(s => s.candidates.some(c => c.previous_overlap_status)).length} スロット
                      </span>が惜しいポイント。</>
                    )}
                  </>
                ) : '評価データを確認しています。'}
              </p>
              <div style={{ fontSize: '12px', color: C.inkMuted, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <span>{slots.length} スロット</span>
                {totalCandidates > slots.length && <span style={{ color: C.carrot }}>{totalCandidates} 候補（{multiSlotCount} スロットが代替あり）</span>}
                <span>評価週 {result.delivery_week || current_week}</span>
              </div>
            </div>
          </div>
        </div>

        {/* セット明細 */}
        {slots.length > 0 && (() => {
          const hasWeekdayScores = Object.values(weekday_scores).some(ws => ws != null)
          const activeDays = DAY_ORDER.filter(d => weekday_scores[d] != null)
          return (
            <div style={{ padding: '32px 56px', borderBottom: `1px solid ${C.line}` }}>
              <h3 style={{ fontFamily: "'Noto Serif JP', serif", fontSize: '20px', fontWeight: 500, margin: '0 0 18px', color: C.ink }}>セット明細</h3>
              <div style={{ display: 'grid', gridTemplateColumns: hasWeekdayScores ? '1fr 180px' : '1fr', gap: '24px', alignItems: 'start' }}>
                {/* 左：品目リスト */}
                <div>
                  {/* 列ヘッダー */}
                  <div style={{ display: 'grid', gridTemplateColumns: '28px 1fr', gap: '8px', marginBottom: '4px', paddingBottom: '4px', borderBottom: `1px solid ${C.line}` }}>
                    <span />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '10px', color: C.inkMuted }}>
                      <span style={{ flex: 1 }}>品名</span>
                      <span style={{ minWidth: '44px', textAlign: 'center' }}>珍しさ</span>
                      <span style={{ minWidth: '52px', textAlign: 'center' }}>曜日</span>
                      <span style={{ minWidth: '34px', textAlign: 'right' }}>満足度</span>
                      <span style={{ minWidth: '44px', textAlign: 'right' }}>ベストワン</span>
                    </div>
                  </div>
                  {slots.map((group, idx) => (
                    <SlotGroupCard key={idx} group={group} slotIdx={idx} C={C} />
                  ))}
                </div>
                {/* 右：曜日別スコアパネル */}
                {hasWeekdayScores && (
                  <div style={{ position: 'sticky', top: '16px' }}>
                    <div style={{ fontSize: '10px', letterSpacing: '0.1em', color: C.inkMuted, marginBottom: '10px', textTransform: 'uppercase' }}>曜日別スコア</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {activeDays.map(day => {
                        const ws = weekday_scores[day]
                        const dColor = scoreColor(ws.delivery / 100, C)
                        return (
                          <div key={day} style={{ background: C.paper2, border: `1px solid ${C.line}`, borderRadius: '8px', padding: '10px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '6px' }}>
                              <span style={{ fontFamily: "'Noto Serif JP', serif", fontSize: '16px', fontWeight: 700, color: C.ink }}>{DAY_LABELS[day]}</span>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '20px', fontWeight: 700, color: dColor, lineHeight: 1 }}>{ws.delivery}</span>
                                <span style={{ fontSize: '9px', color: C.inkMuted }}>{ws.count}品</span>
                              </div>
                            </div>
                            <ScoreBreakdownBar breakdown={ws.breakdown} height={6} C={C} />
                            <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              {INDICATOR_CATEGORIES.map(cat => {
                                const pts = ws.breakdown?.[cat.key]
                                if (pts == null) return null
                                const color = CATEGORY_META[cat.key]?.color
                                return (
                                  <div key={cat.key} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10px', color: C.inkSoft }}>
                                    <span style={{ width: '6px', height: '6px', borderRadius: '1px', background: color, flexShrink: 0 }} />
                                    <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{CATEGORY_META[cat.key]?.label}</span>
                                    <span style={{ fontFamily: "'JetBrains Mono', monospace", color: C.ink, fontWeight: 600 }}>{pts.toFixed(1)}</span>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        })()}

        {/* クイックバー */}
        <QuickBar categories={INDICATOR_CATEGORIES} activeKey={activeCategory} onSelect={setActiveCategory} result={result} C={C} />

        {/* 指標セクション（選択カテゴリのみ表示） */}
        {INDICATOR_CATEGORIES.map((cat, idx) => {
          if (cat.key !== activeCategory) return null
          const catDetails = (result.details || []).filter(d => cat.indicatorKeys.includes(d.key))
          const catScore   = categoryScore(result, cat.indicatorKeys)
          const comment    = genComment(cat.key)

          let visual = null
          switch (cat.key) {
            case 'seasonality': visual = <SeasonalityVisual slots={slots} C={C} />; break
            case 'duplication': visual = <DuplicationVisual slots={slots} pastSets={past_sets} C={C} />; break
            case 'rarity':      visual = <RarityVisual slots={slots} C={C} />; break
            case 'balance':     visual = <ColorVisual slots={slots} C={C} />; break
            case 'popularity':  visual = <PopularityVisual slots={slots} C={C} />; break
          }

          return (
            <IndicatorSection
              key={cat.key}
              sectionNum={idx + 1}
              title={cat.label}
              score={catScore}
              visual={visual}
              comment={comment}
              details={catDetails}
              C={C}
            />
          )
        })}

        {/* フッター */}
        <div style={{ padding: '20px 56px', background: C.paper2, display: 'flex', gap: '12px', justifyContent: 'flex-end', borderTop: `1px solid ${C.line}` }}>
          <Link
            href={`/weekly_evaluations/${current_week}`}
            style={{ fontFamily: 'inherit', fontSize: '13px', fontWeight: 500, padding: '8px 16px', borderRadius: '6px', border: `1px solid ${C.line}`, background: C.paper, color: C.inkSoft, textDecoration: 'none' }}
          >
            ← 週次一覧に戻る
          </Link>
          <button
            onClick={handleReEvaluate}
            style={{ fontFamily: 'inherit', fontSize: '13px', fontWeight: 500, padding: '8px 18px', borderRadius: '6px', border: `1px solid ${C.greenDark}`, background: C.greenDark, color: C.paper, cursor: 'pointer' }}
          >
            再評価
          </button>
        </div>
      </div>
    </AppLayout>
  )
}
